import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { ListingCard } from "@/components/listing-card";
import { Empty, ErrorNotice, Icon, Loading } from "@/components/ui";
import { api } from "@/lib/api";
import type { Page } from "@/lib/types";
import type { Product } from "@/lib/shop";

export function HomePage() {
  const [kind, setKind] = useState("");
  const listings = useQuery({
    queryKey: ["listings", "home", kind],
    queryFn: () => api<Page<Product>>("/shop/catalog/?" + new URLSearchParams({ kind })),
  });
  return <>
    <section className="hero shell">
      <div className="hero-copy">
        <p className="eyebrow"><span className="small-line" /> LA SECONDE MAIN, AUTREMENT</p>
        <h1>Le beau ne<br />se démode <em>pas.</em></h1>
        <p className="hero-description">Des pièces de caractère, choisies avec soin.<br />
          Du mobilier à la mode, faites de la place<br className="desktop-break" /> à de nouvelles histoires.</p>
        <Link to="/catalogue" className="button button-dark">Trouver ma prochaine pièce <Icon name="arrow" size={18} /></Link>
        <div className="hero-footnote"><span className="little-star">✳</span><span>Une autre façon de consommer.<br /><strong>Une belle façon de se rencontrer.</strong></span></div>
      </div>
      <div className="hero-visual">
        <img className="hero-image" src="/images/hero.jpg" alt="Un salon lumineux aux matières naturelles, avec un canapé et du mobilier de caractère" fetchPriority="high" width="1100" height="1200" />
        <div className="hero-stamp">BELLES PIÈCES<br /><span>seconde vie</span><br />DAKAR · SÉNÉGAL</div>
        <div className="hero-caption"><span>Chaque pièce a une histoire.</span><em>La prochaine pourrait être la vôtre.</em></div>
      </div>
    </section>
    <div className="promise-strip"><div className="shell">
      <span><Icon name="shield" /> Des annonces examinées par notre équipe</span>
      <span><Icon name="pin" /> Des trouvailles près de chez vous, à Dakar</span>
      <span><Icon name="leaf" /> Moins de neuf. Plus de sens.</span>
    </div></div>
    <section className="section shell">
      <div className="section-heading"><div><p className="eyebrow">DEUX UNIVERS, UNE MÊME EXIGENCE</p><h2>À chaque envie, <em>sa pièce.</em></h2></div>
        <p className="section-aside">Le plaisir de trouver ce qu'on<br />ne cherchait pas encore.</p></div>
      <div className="universe-grid">
        <Link to="/catalogue" search={{ kind: "furniture" }} className="universe-card">
          <img src="/images/interior.jpg" alt="Mobilier chaleureux et objets de décoration" loading="lazy" width="1200" height="800" />
          <div><p>POUR LES LIEUX QUI VOUS RESSEMBLENT</p><h3>Mobilier & décoration</h3><span>Explorer l'univers <Icon name="arrow" /></span></div>
        </Link>
        <Link to="/catalogue" search={{ kind: "fashion" }} className="universe-card">
          <img src="/images/fashion.jpg" alt="Une sélection de vêtements et accessoires aux tons naturels" loading="lazy" width="1200" height="800" />
          <div><p>LE STYLE TRAVERSE LES SAISONS</p><h3>Mode & luxe</h3><span>Explorer l'univers <Icon name="arrow" /></span></div>
        </Link>
      </div>
    </section>
    <section className="section shell arrivals">
      <div className="section-heading"><div><p className="eyebrow">FRAÎCHEMENT ARRIVÉES</p><h2>De belles <em>rencontres.</em></h2></div>
        <Link to="/catalogue" className="text-link">Voir toutes les pièces <Icon name="arrow" size={17} /></Link></div>
      <div className="filter-tabs" role="group" aria-label="Filtrer les nouvelles pièces">
        {[["", "Toutes les pièces"], ["furniture", "Mobilier & déco"], ["fashion", "Mode & luxe"]].map(([value, label]) =>
          <button key={value} className={kind === value ? "active" : ""} aria-pressed={kind === value} onClick={() => setKind(value ?? "")}>{label}</button>)}
      </div>
      <ErrorNotice error={listings.error} />
      {listings.isPending ? <Loading /> : listings.data?.results.length
        ? <div className="product-grid">{listings.data.results.slice(0, 8).map((listing) => <ListingCard key={listing.id} listing={listing} />)}</div>
        : !listings.error && <Empty title="Les prochaines histoires se préparent."><p>Et si votre pièce était la première ?</p><Link to="/deposer" className="button button-dark">Déposer une annonce</Link></Empty>}
    </section>
    <section className="story-band"><div className="shell story-grid">
      <div><p className="eyebrow">UN OBJET QUI DORT CHEZ VOUS…</p><h2>…fera peut-être<br /><em>battre un autre cœur.</em></h2>
        <p>Un fauteuil qui ne trouve plus sa place. Un sac que vous ne portez plus. Offrez-leur une nouvelle vie, simplement.</p>
        <Link to="/deposer" className="button button-dark">Je vends avec Revi <Icon name="arrow" size={18} /></Link></div>
      <ol className="steps-editorial">
        <li><span>01</span><div><h3>Racontez votre pièce.</h3><p>Quelques photos, les détails qui comptent et votre prix.</p></div></li>
        <li><span>02</span><div><h3>On s'occupe de la sélection.</h3><p>Notre équipe examine votre annonce avant sa publication.</p></div></li>
        <li><span>03</span><div><h3>Votre pièce trouve sa nouvelle histoire.</h3><p>Les acheteurs commandent et règlent directement sur Revi. Suivez la vente depuis votre espace.</p></div></li>
      </ol>
    </div></section>
  </>;
}
